<template>
  <div class="category-management">
    <div class="toolbar">
      <el-input v-model="keyword" placeholder="搜索分类名称或编码" clearable class="toolbar-input" />
      <el-select
        v-if="!hideScopeFilter"
        v-model="scopeType"
        clearable
        placeholder="适用范围"
        class="toolbar-select"
      >
        <el-option label="全部" value="" />
        <el-option label="维修工单" value="MAINTENANCE" />
        <el-option v-if="FEATURE_PRODUCTION_ORDERS" label="生产工单" value="PRODUCTION" />
      </el-select>
      <el-button type="primary" :loading="loading" @click="fetchCategories">刷新</el-button>
      <el-button type="success" @click="openCreate">新增分类</el-button>
    </div>

    <el-table :data="paginatedCategories" v-loading="loading" border stripe size="small">
      <el-table-column prop="name" label="分类名称" min-width="160" />
      <el-table-column prop="code" label="分类编码" min-width="140" />
      <el-table-column label="上级分类" min-width="140">
        <template #default="{ row }">
          {{ row.parentName || '一级分类' }}
        </template>
      </el-table-column>
      <el-table-column label="适用范围" width="120">
        <template #default="{ row }">
          {{ getScopeText(row.scopeType) }}
        </template>
      </el-table-column>
      <el-table-column label="推荐严重度" width="120">
        <template #default="{ row }">
          {{ getLevelText(row.severityHint) }}
        </template>
      </el-table-column>
      <el-table-column prop="sortOrder" label="排序" width="90" />
      <el-table-column label="启用" width="90">
        <template #default="{ row }">
          <el-tag :type="row.enabled ? 'success' : 'info'">{{ row.enabled ? '是' : '否' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="说明" min-width="180" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" text @click="openEdit(row)">编辑</el-button>
          <el-button type="danger" text @click="removeCategory(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-wrapper">
      <el-pagination
        v-model:current-page="currentPage"
        :page-size="pageSize"
        layout="total, prev, pager, next"
        :total="filteredCategories.length"
      />
    </div>

    <el-dialog v-model="dialogVisible" :title="isEditing ? '编辑工单分类' : '新增工单分类'" width="520px">
      <el-form :model="form" label-width="96px">
        <el-form-item label="分类名称" required>
          <el-input v-model="form.name" maxlength="100" />
        </el-form-item>
        <el-form-item label="分类编码" required>
          <el-input v-model="form.code" maxlength="50" />
        </el-form-item>
        <el-form-item label="适用范围" required>
          <el-select v-model="form.scopeType" style="width: 100%" :disabled="!!fixedScopeType">
            <el-option label="维修工单" value="MAINTENANCE" />
            <el-option v-if="FEATURE_PRODUCTION_ORDERS" label="生产工单" value="PRODUCTION" />
          </el-select>
        </el-form-item>
        <el-form-item label="上级分类">
          <el-select
            v-model="form.parentId"
            clearable
            filterable
            style="width: 100%"
            placeholder="不选则为一级分类"
          >
            <el-option
              v-for="item in parentOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="严重程度">
          <el-select v-model="form.severityHint" clearable style="width: 100%">
            <el-option label="高" value="HIGH" />
            <el-option label="中" value="MEDIUM" />
            <el-option label="低" value="LOW" />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sortOrder" :min="0" :max="999" style="width: 100%" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.enabled" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="form.description" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { WorkOrderCategoriesAPI } from '@/request/workOrderCategories'
import { lowerText, trimText } from '@/utils/text'
import { FEATURE_PRODUCTION_ORDERS } from '@/config/featureFlags'

const createEmptyForm = () => ({
  id: null,
  name: '',
  code: '',
  scopeType: 'MAINTENANCE',
  parentId: null,
  severityHint: '',
  sortOrder: 0,
  enabled: true,
  description: ''
})

export default {
  name: 'WorkOrderCategoryManagement',
  props: {
    fixedScopeType: {
      type: String,
      default: ''
    },
    hideScopeFilter: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const loading = ref(false)
    const submitting = ref(false)
    const dialogVisible = ref(false)
    const isEditing = ref(false)
    const keyword = ref('')
    const scopeType = ref('')
    const currentPage = ref(1)
    const pageSize = 10
    const categories = ref([])
    const form = reactive(createEmptyForm())
    const parentOptions = computed(() =>
      categories.value.filter((item) =>
        item.scopeType === form.scopeType &&
        !item.parentId &&
        item.id !== form.id
      )
    )

    watch(() => form.scopeType, () => {
      if (!parentOptions.value.some((item) => item.id === form.parentId)) {
        form.parentId = null
      }
    })

    const fetchCategories = async () => {
      try {
        loading.value = true
        const effectiveScopeType = props.fixedScopeType || scopeType.value
        const list = await WorkOrderCategoriesAPI.list(effectiveScopeType ? { scopeType: effectiveScopeType } : {})
        categories.value = Array.isArray(list) ? list : []
      } catch (err) {
        ElMessage.error(err?.message || '获取工单分类失败')
      } finally {
        loading.value = false
      }
    }

    const filteredCategories = computed(() => {
      const kw = lowerText(keyword.value)
      if (!kw) return categories.value
      return categories.value.filter((item) => `${item.name || ''} ${item.code || ''}`.toLowerCase().includes(kw))
    })

    const paginatedCategories = computed(() => {
      const start = (currentPage.value - 1) * pageSize
      return filteredCategories.value.slice(start, start + pageSize)
    })

    const openCreate = () => {
      Object.assign(form, createEmptyForm(), {
        scopeType: props.fixedScopeType || createEmptyForm().scopeType
      })
      isEditing.value = false
      dialogVisible.value = true
    }

    const openEdit = (row) => {
      Object.assign(form, createEmptyForm(), row)
      isEditing.value = true
      dialogVisible.value = true
    }

    const submitForm = async () => {
      if (!trimText(form.name) || !trimText(form.code)) {
        ElMessage.warning('请填写分类名称和分类编码')
        return
      }
      try {
        submitting.value = true
        const payload = {
          name: trimText(form.name),
          code: trimText(form.code),
          scopeType: form.scopeType,
          parentId: form.parentId || null,
          severityHint: form.severityHint || null,
          sortOrder: form.sortOrder ?? 0,
          enabled: !!form.enabled,
          description: trimText(form.description) || null
        }
        if (isEditing.value) {
          await WorkOrderCategoriesAPI.update(form.id, payload)
          ElMessage.success('分类已更新')
        } else {
          await WorkOrderCategoriesAPI.create(payload)
          ElMessage.success('分类已创建')
        }
        dialogVisible.value = false
        await fetchCategories()
      } catch (err) {
        ElMessage.error(err?.message || '保存分类失败')
      } finally {
        submitting.value = false
      }
    }

    const removeCategory = async (row) => {
      try {
        await ElMessageBox.confirm(`确定删除分类“${row.name}”吗？`, '提示', {
          type: 'warning'
        })
        await WorkOrderCategoriesAPI.deleteById(row.id)
        ElMessage.success('分类已删除')
        await fetchCategories()
      } catch (err) {
        if (err !== 'cancel' && err !== 'close') {
          ElMessage.error(err?.message || '删除分类失败')
        }
      }
    }

    const getScopeText = (value) => ({
      MAINTENANCE: '维修工单',
      PRODUCTION: '生产工单'
    }[value] || value || '—')

    const getLevelText = (value) => ({
      HIGH: '高',
      MEDIUM: '中',
      LOW: '低'
    }[value] || '—')

    onMounted(fetchCategories)

    if (props.fixedScopeType) {
      scopeType.value = props.fixedScopeType
    }

    watch(filteredCategories, (list) => {
      const totalPages = Math.max(1, Math.ceil(list.length / pageSize))
      if (currentPage.value > totalPages) {
        currentPage.value = totalPages
      } else {
        currentPage.value = 1
      }
    })

    return {
      FEATURE_PRODUCTION_ORDERS,
      loading,
      submitting,
      dialogVisible,
      isEditing,
      keyword,
      scopeType,
      currentPage,
      pageSize,
      fixedScopeType: props.fixedScopeType,
      categories,
      filteredCategories,
      paginatedCategories,
      parentOptions,
      form,
      fetchCategories,
      openCreate,
      openEdit,
      submitForm,
      removeCategory,
      getScopeText,
      getLevelText
    }
  }
}
</script>

<style scoped>
.category-management {
  padding: 8px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}

.toolbar-input {
  width: 280px;
}

.toolbar-select {
  width: 140px;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
